import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  VersicherungssummeLinieForWertsachenSumme,
  WertsachensummenRequestModel,
  WertsachensummenResponseModel,
} from '@ps20/bff-models';
import {
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  VhvRelevantVorbelegungdaten,
  VhvVorbelegungdaten,
  Vorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  VersicherungssummeForLinie,
  VersicherungssummenResponseReceived,
} from '../action-types/versicherungssummenResponseReceived';
import { IAppState } from '../appState';
import { VersicherungsSummeGrenzen } from '../model/versicherungssummen';
import { VhvAction, VhvActions } from '../vhvActions';
import { selectVhvRelevantVorbelegungdaten } from '../../app/shared/selectors/transient-state.selector';
import { selectVertragsoptionen } from '../../app/shared/selectors/vertragsoption.selector';
import { VersicherungssummeEnum, VertragsoptionenVHV } from '../model/vertragsoptionen.model';
import { hasAdresseChanged, hasVerssummeChanged } from './versicherungssummenSaga';

export const WERTSACHENSUMMEN_URL = '/privatschutz/api/wertsachensummen';

export enum WERTSACHENSUMMEN_SERVICE_REQUEST_EVENTS {
  WERTSACHENSUMMEN_REQUEST_SUCCESSFUL = 'WERTSACHENSUMMEN_REQUEST_SUCCESSFUL',
}

export function* watchWertsachensummenTrigger() {
  yield takeLatest(
    VhvActions.VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED,
    safeSaga(requestWertsachensummenOnVersicherungssummenChange, App.VHV),
  );
  yield takeLatest(
    GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED,
    safeSaga(requestWertsachensummenOnBeibehaltungRequested, App.VHV),
  );
  yield takeEvery(
    WERTSACHENSUMMEN_SERVICE_REQUEST_EVENTS.WERTSACHENSUMMEN_REQUEST_SUCCESSFUL,
    safeSaga(wertsachensummenRequestSuccessful, App.VHV),
  );
}

export function* requestWertsachensummenOnBeibehaltungRequested(
  action: GlobalAction<Vorbelegungdaten>,
) {
  const state: IAppState = yield select();
  const vhvVorbelegungdaten = action.payload as VhvVorbelegungdaten;
  if (
    action.payload.forApp !== App.VHV ||
    action.payload.angebotsId !== state.angebotsId ||
    vhvVorbelegungdaten.wertsachensumme
  ) {
    return;
  }
  const versicherungssumme = state.vertragsoptionen.versicherungssumme;
  if (versicherungssumme <= VersicherungsSummeGrenzen.MAX_VERSSUMME) {
    yield requestWertsachensummen(state.versicherungssummen);
  }
}

export function* requestWertsachensummenOnVersicherungssummenChange(
  action: VhvAction<VersicherungssummenResponseReceived>,
) {
  const versicherungssummenForLinien = action.payload.summen;

  yield requestWertsachensummen(versicherungssummenForLinien);
}

export function* requestWertsachensummen(versicherungssummen: VersicherungssummeForLinie[]) {
  const mappedVersicherungssummen: VersicherungssummeLinieForWertsachenSumme[] =
    versicherungssummen.map(elem => {
      return {
        produktlinie: elem.produktlinie,
        versicherungssumme: elem.summe,
      };
    });
  const state: IAppState = yield select();
  const wertsachensummenRequest: WertsachensummenRequestModel = {
    versicherungssummen: mappedVersicherungssummen,
    wohnungStaendigBewohnt: state.versObjekt.staendigBewohnt,
  };

  const relevantVorbelegungdaten: VhvRelevantVorbelegungdaten =
    selectVhvRelevantVorbelegungdaten(state);
  const vertragsoptionen: VertragsoptionenVHV = selectVertragsoptionen(state);
  const userChangedVorbelegteDaten: boolean =
    hasAdresseChanged(state, relevantVorbelegungdaten) &&
    hasVerssummeChanged(state, vertragsoptionen?.versicherungssumme);

  if (
    !userChangedVorbelegteDaten &&
    relevantVorbelegungdaten?.wertsachensumme != null &&
    state.vertragsoptionen.versicherungssummeArt === VersicherungssummeEnum.BISHERIGE_VERSSUMME
  ) {
    wertsachensummenRequest.bisherigeWertsachenSumme = relevantVorbelegungdaten.wertsachensumme;
  }

  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.VHV,
      angebotsId: state.angebotsId,
      body: wertsachensummenRequest,
      url: WERTSACHENSUMMEN_URL,
      successEvent: WERTSACHENSUMMEN_SERVICE_REQUEST_EVENTS.WERTSACHENSUMMEN_REQUEST_SUCCESSFUL,
      errorEvent: null,
    }),
  );
}

export function* wertsachensummenRequestSuccessful(
  action: DoRequestResponse<WertsachensummenResponseModel>,
) {
  const angebotsId = action.doRequestAction.payload.angebotsId;
  const state: IAppState = yield select();
  if (angebotsId !== state.angebotsId) {
    return;
  }
  const extendedWertsachensummenByEditableAttribute = action.payload.wertsachensummen.map(
    wertsachensumme => ({
      ...wertsachensumme,
      produktline: wertsachensumme.produktline,
      editable:
        wertsachensumme.maxSumme > wertsachensumme.summe ||
        wertsachensumme.summe > wertsachensumme.minSumme,
    }),
  );
  const changeWertsachensummenAction = !state.vertragsoptionen?.beibehalten
    ? new VhvActions().wertsachensummenChanged({
        wertsachensummen: extendedWertsachensummenByEditableAttribute,
      })
    : new VhvActions().wertsachensummenChangedAfterVorbelegung({
        wertsachensummen: extendedWertsachensummenByEditableAttribute,
      });
  yield put(changeWertsachensummenAction);
}
